/**
 * index.tsx
 *
 * Author: Maurice T. Meyer
 * E-Mail: maurice@lavireo.com
 *
 * (c) Laviréo. All rights reserved.
 *
 * This document is the property of Laviréo.
 * It is considered confidential and proprietary.
 *
 * This document may not be reproduced or transmitted in any form,
 * in whole or in part, without the express written permission of
 * Laviréo.
 */

import { FC, MouseEvent, useMemo, useState } from 'react';
import * as yup                              from 'yup';
import { Formik, Form }                      from 'formik';
import apiFetch                              from '@wordpress/api-fetch';
import { Button }                            from '~/components/button';
import { steps }                             from './const';
import { styles }                            from './styles';

const initialValues = {
  address:            '',

  propertyType:       'apartment', // 'house' | 'apartment'
  houseType:          'single', // Only if propertyType is 'house'

  numberOfFloors:     '1', // Differentiate between house and apartment
  floor:              '1', // Only if propertyType is 'apartment'

  livingSpace:        '',
  propertySize:       '',

  yearOfConstruction: '',

  numberOfRooms:      '1',
  numberOfBathrooms:  '1',

  extras:             [],
  comment:            '',

  firstName:          '',
  lastName:           '',
  email:              '',
  phone:              ''
};

const validationSchemas = [
  yup.object({
    address: yup.string().required('Adresse ist erforderlich')
  }),

  yup.object({
    propertyType:       yup.string().oneOf(['house', 'apartment']),
    houseType:          yup.string().when('propertyType', {
      is: 'house',
      then: schema => schema.oneOf(['single', 'row', 'duplex']).required('Haustyp ist erforderlich')
    }),

    numberOfFloors:     yup.number().min(1, 'Mindestens 1 Etage erforderlich').required('Anzahl der Etagen ist erforderlich'),
    floor:              yup.number().when('propertyType', {
      is: 'apartment',
      then: schema => schema.min(1, 'Mindestens 1. Etage erforderlich').required('Etage ist erforderlich')
    }),

    livingSpace:        yup.number().min(10, 'Mindestgröße ist 10 m²').required('Wohnfläche ist erforderlich'),
    propertySize:       yup.number().when('propertyType', {
      is: 'house',
      then: schema => schema.min(10, 'Mindestgröße ist 10 m²').moreThan(yup.ref("livingSpace"), "Grundstücksfläche").required('Grundstücksfläche ist erforderlich')
    }),
    yearOfConstruction: yup.number().integer().min(1600, 'Jahr muss nach 1600 liegen').max(new Date().getFullYear(), 'Jahr darf nicht in der Zukunft liegen').required('Baujahr ist erforderlich'),

    numberOfRooms:      yup.number().min(1, 'Mindestens 1 Zimmer erforderlich').required('Anzahl der Zimmer ist erforderlich'),
    numberOfBathrooms:  yup.number().min(1, 'Mindestens 1 Bad erforderlich').required('Anzahl der Bäder ist erforderlich'),
  }),

  yup.object({
    extras:             yup.array().of(yup.string()).optional(),
    comment:            yup.string().optional(),
  }),

  yup.object({
    firstName:          yup.string().required('Vorname ist erforderlich'),
    lastName:           yup.string().required('Nachname ist erforderlich'),
    email:              yup.string().email('Ungültige E-Mail-Adresse').required('E-Mail ist erforderlich'),
    phone:              yup.string().optional(),
  })
];

export const Rating: FC = () => {
  window.scrollTo(0, 0);

  const [step, setStep] = useState(0);
  const handlePrevClick = (ev: MouseEvent) => {
    ev.preventDefault();
    setStep(s => Math.max(0, s - 1))
  };

  const handleSubmit = async (data: any, actions: any) => {
    window.scrollTo(0, 0);

    if (step === steps.length - 1) {
      setStep(0);
      return;
    }

    if (step < steps.length - 2) {
      actions.setSubmitting(false);
      setStep(s => s + 1);
      return;
    }

    /**
     * Submit the form data to the server or handle it as needed. */
    await apiFetch({ data, path: '/wp-json/rating/v1/ratings', parse: false, method: 'POST' })
      .then(
        () => {
          actions.resetForm();
          setStep(s => s + 1);
        },
        () => {
          
        }
      );

    actions.setSubmitting(false);
  }

  const { title, component: StepComponent, nextLabel, withBack = true } = useMemo(() => steps[step], [step]);
  

  return (
    <div css={styles.container}>
      <Formik onSubmit={handleSubmit} initialValues={initialValues} validationSchema={validationSchemas[step]} validateOnChange={true}>
        { 
          props => (
            <Form>
              <div css={styles.header}>
                <h1 css={styles.title}>{title}</h1>
              </div>
              <div css={styles.content}>
                <StepComponent />
              </div>
              <div css={styles.navigation}>
                { withBack &&
                  <Button onClick={handlePrevClick} type="secondary" disabled={step === 0}>Zurück</Button>
                }
                <Button isFull={!withBack} type="primary" disabled={props.isValidating || !props.isValid} role="submit">{nextLabel}</Button>
              </div>
            </Form>
          )
        }
      </Formik>
    </div>
  );
};
